// src/hooks/useReservas.js

import { useState, useEffect, useCallback } from 'react';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, format } from 'date-fns';

export function useReservas(companyId, selectedDate, view) {
  const [data, setData] = useState({ settings: null, bookings: [], companyData: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const db = getFirestore();

  const fetchReservasData = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);
    
    try {
      const companyRef = doc(db, 'companies', companyId);
      const settingsRef = doc(db, 'companies', companyId, 'reservationSettings', 'config');

      const [companySnap, settingsSnap] = await Promise.all([
        getDoc(companyRef),
        getDoc(settingsRef),
      ]);

      if (!companySnap.exists()) throw new Error('La empresa no existe.');
      if (!settingsSnap.exists()) throw new Error('No se encontró la configuración de reservas.');

      const companyData = companySnap.data();
      const settingsData = settingsSnap.data();

      const bookingsRef = collection(db, 'companies', companyId, 'bookings');
      let q;

      if (view === 'day') {
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');
        q = query(bookingsRef, where("date", "==", formattedDate));
      } else {
        let startDate, endDate;
        if (view === 'week') {
          startDate = startOfWeek(selectedDate, { weekStartsOn: 1 });
          endDate = endOfWeek(selectedDate, { weekStartsOn: 1 });
        } else { // month
          startDate = startOfMonth(selectedDate);
          endDate = endOfMonth(selectedDate);
        }
        q = query(bookingsRef, 
          where("bookingTimestamp", ">=", Timestamp.fromDate(startDate)),
          where("bookingTimestamp", "<=", Timestamp.fromDate(endDate))
        );
      }
      
      const bookingsSnap = await getDocs(q);
      const bookingsData = bookingsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setData({ settings: settingsData, bookings: bookingsData, companyData: companyData });

    } catch (err) {
      setError(err.message);
      console.error("Error en useReservas:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedDate, view, db]);

  useEffect(() => {
    fetchReservasData();
  }, [fetchReservasData]);

  return { ...data, loading, error, refetchReservas: fetchReservasData };
}